import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import {
  Hotel, Scan, Shield, Users, BarChart3, Clock, CheckCircle2,
  ArrowRight, Star, Menu, X, Mail, Phone, MapPin,
  Zap, Globe, Lock, Smartphone, FileText, CreditCard,
  ChevronRight, Play, Sparkles, Building2, Receipt,
  UserCheck, BedDouble, ClipboardCheck, TrendingUp,
  ChevronDown, MousePointer, ArrowUpRight, Quote,
  Check, XIcon, Monitor, Tablet, IndianRupee, Languages,
  Rocket, Timer, ShieldCheck, Heart, MessageCircle, Calendar, Crown
} from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import LanguageSwitcher from '@/components/LanguageSwitcher';

// Animation hook for scroll reveal
const useScrollReveal = (threshold = 0.1) => {
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(entry.target);
        }
      },
      { threshold }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, [threshold]);

  return [ref, isVisible];
};

// Animated counter component
const AnimatedCounter = ({ end, suffix = '', duration = 2000 }) => {
  const [count, setCount] = useState(0);
  const [ref, isVisible] = useScrollReveal();

  useEffect(() => {
    if (!isVisible) return;
    
    let startTime;
    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const progress = Math.min((currentTime - startTime) / duration, 1);
      setCount(Math.floor(progress * end));
      if (progress < 1) requestAnimationFrame(animate);
    };
    requestAnimationFrame(animate);
  }, [isVisible, end, duration]);

  return <span ref={ref}>{count}{suffix}</span>;
};

export default function LandingPage() {
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [proSetupExpanded, setProSetupExpanded] = useState(false);
  
  // Walkthrough video URLs based on language - served from our backend
  const API = process.env.REACT_APP_BACKEND_URL;
  const walkthroughVideos = {
    en: `${API}/api/demo-video/stream/en`,
    ta: `${API}/api/demo-video/stream/en`,
    ml: `${API}/api/demo-video/stream/en`,
    hi: `${API}/api/demo-video/stream/hi`,
    kn: `${API}/api/demo-video/stream/en`,
    te: `${API}/api/demo-video/stream/en`
  };
  
  const currentVideoUrl = walkthroughVideos[language] || walkthroughVideos.en;
  
  // Contact form state
  const [contactForm, setContactForm] = useState({
    name: '',
    hotel_name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [contactLoading, setContactLoading] = useState(false);

  const handleDemoSignup = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error(t('landing.errorMessages.enterEmail'));
      return;
    }
    navigate(`/register?email=${encodeURIComponent(email)}`);
  };

  const handleContactChange = (e) => {
    const { name, value } = e.target;
    setContactForm(prev => ({ ...prev, [name]: value }));
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    if (!contactForm.name.trim()) {
      toast.error(t('landing.errorMessages.enterName'));
      return;
    }
    if (!contactForm.email.trim()) {
      toast.error(t('landing.errorMessages.enterEmail'));
      return;
    }
    if (!contactForm.message.trim() || contactForm.message.length < 10) {
      toast.error(t('landing.errorMessages.enterMessage'));
      return;
    }
    
    setContactLoading(true);
    try {
      const response = await axios.post(`${API}/api/contact/submit`, contactForm);
      if (response.data.success) {
        toast.success(response.data.message || t('landing.messageSent'));
        setContactForm({ name: '', hotel_name: '', email: '', phone: '', message: '' });
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || t('landing.errorMessages.sendFailed');
      toast.error(errorMsg);
    } finally {
      setContactLoading(false);
    }
  };

  // Feature data - using translations
  const features = [
    {
      icon: <Scan className="w-7 h-7" />,
      title: t('landing.featureSection.aiOcr.title'),
      subtitle: t('landing.featureSection.aiOcr.subtitle'),
      description: t('landing.featureSection.aiOcr.description'),
      highlights: [t('landing.featureSection.aiOcr.highlight1'), t('landing.featureSection.aiOcr.highlight2'), t('landing.featureSection.aiOcr.highlight3')],
    },
    {
      icon: <Users className="w-7 h-7" />,
      title: t('landing.featureSection.guestManagement.title'),
      subtitle: t('landing.featureSection.guestManagement.subtitle'),
      description: t('landing.featureSection.guestManagement.description'),
      highlights: [t('landing.featureSection.guestManagement.highlight1'), t('landing.featureSection.guestManagement.highlight2'), t('landing.featureSection.guestManagement.highlight3')],
    },
    {
      icon: <BedDouble className="w-7 h-7" />,
      title: t('landing.featureSection.roomManagement.title'),
      subtitle: t('landing.featureSection.roomManagement.subtitle'),
      description: t('landing.featureSection.roomManagement.description'),
      highlights: [t('landing.featureSection.roomManagement.highlight1'), t('landing.featureSection.roomManagement.highlight2'), t('landing.featureSection.roomManagement.highlight3')],
    },
    {
      icon: <BarChart3 className="w-7 h-7" />,
      title: t('landing.featureSection.reports.title'),
      subtitle: t('landing.featureSection.reports.subtitle'),
      description: t('landing.featureSection.reports.description'),
      highlights: [t('landing.featureSection.reports.highlight1'), t('landing.featureSection.reports.highlight2'), t('landing.featureSection.reports.highlight3')],
    },
    {
      icon: <Shield className="w-7 h-7" />,
      title: t('landing.featureSection.compliance.title'),
      subtitle: t('landing.featureSection.compliance.subtitle'),
      description: t('landing.featureSection.compliance.description'),
      highlights: [t('landing.featureSection.compliance.highlight1'), t('landing.featureSection.compliance.highlight2'), t('landing.featureSection.compliance.highlight3')],
    },
    {
      icon: <Receipt className="w-7 h-7" />,
      title: t('landing.featureSection.billing.title'),
      subtitle: t('landing.featureSection.billing.subtitle'),
      description: t('landing.featureSection.billing.description'),
      highlights: [t('landing.featureSection.billing.highlight1'), t('landing.featureSection.billing.highlight2'), t('landing.featureSection.billing.highlight3')],
    },
    {
      icon: <MessageCircle className="w-7 h-7" />,
      title: t('landing.featureSection.integrations.title'),
      subtitle: t('landing.featureSection.integrations.subtitle'),
      description: t('landing.featureSection.integrations.description'),
      highlights: [t('landing.featureSection.integrations.highlight1'), t('landing.featureSection.integrations.highlight2'), t('landing.featureSection.integrations.highlight3')],
    }
  ];

  // Comparison data - using translations
  const comparisonData = [
    { feature: t('landing.whyMerinaSection.comparison.mobileFirst'), merina: true, traditional: false },
    { feature: t('landing.whyMerinaSection.comparison.gstBilling'), merina: true, traditional: "partial" },
    { feature: t('landing.whyMerinaSection.comparison.localLanguage'), merina: true, traditional: false },
    { feature: t('landing.whyMerinaSection.comparison.aiScanning'), merina: true, traditional: false },
    { feature: t('landing.whyMerinaSection.comparison.whatsappCalendar'), merina: true, traditional: false },
    { feature: t('landing.whyMerinaSection.comparison.setupTime'), merina: t('landing.whyMerinaSection.comparison.setupTimeMerina'), traditional: t('landing.whyMerinaSection.comparison.setupTimeTraditional') },
    { feature: t('landing.whyMerinaSection.comparison.builtFor'), merina: true, traditional: false },
    { feature: t('landing.whyMerinaSection.comparison.noItTeam'), merina: true, traditional: false },
  ];

  // Pricing plans - aligned with NEW backend pricing (January 2025)
  // Full pricing display - shows complete price (₹1,299, ₹1,899, ₹2,899)
  // BILLING CYCLES: Quarterly, Half-Yearly, Yearly (NO MONTHLY)
  const [billingCycle, setBillingCycle] = useState('quarterly'); // quarterly, half_yearly, yearly
  
  // Calculate price based on billing cycle
  const getPriceForCycle = (baseMonthly, cycle) => {
    switch(cycle) {
      case 'quarterly':
        return baseMonthly * 3; // No discount
      case 'half_yearly':
        return Math.round(baseMonthly * 6 * 0.9); // 10% discount
      case 'yearly':
        return Math.round(baseMonthly * 12 * 0.8); // 20% discount
      default:
        return baseMonthly * 3;
    }
  };
  
  const getCycleLabel = (cycle) => {
    switch(cycle) {
      case 'quarterly': return t('landing.pricingSection.quarterly') || '3 months';
      case 'half_yearly': return t('landing.pricingSection.halfYearly') || '6 months';
      case 'yearly': return t('landing.pricingSection.yearly') || '12 months';
      default: return '3 months';
    }
  };
  
  const getDiscountBadge = (cycle) => {
    switch(cycle) {
      case 'half_yearly': return '10% OFF';
      case 'yearly': return '20% OFF';
      default: return null;
    }
  };
  
  const pricingPlans = [
    {
      name: t('landing.pricingSection.starter.name'),
      baseMonthlyPrice: 1299,  // ₹1,299/month base
      description: t('landing.pricingSection.starter.description'),
      features: [
        t('landing.pricingSection.starter.feature1') || '200 OCR scans/month',
        t('landing.pricingSection.starter.feature2') || '2 user accounts',
        t('landing.pricingSection.starter.feature3') || '20 rooms max',
        t('landing.pricingSection.starter.feature4') || 'Invoice generation',
        t('landing.pricingSection.starter.feature5') || 'Reports & Analytics',
        t('landing.pricingSection.starter.feature6') || 'Expenses tracking'
      ],
      cta: t('landing.pricingSection.starter.cta'),
      popular: false,
      maxUsers: 2,
      ocrLimit: 200
    },
    {
      name: t('landing.pricingSection.professional.name'),
      baseMonthlyPrice: 1899,  // ₹1,899/month base
      description: t('landing.pricingSection.professional.description'),
      features: [
        t('landing.pricingSection.professional.feature1') || '600 OCR scans/month',
        t('landing.pricingSection.professional.feature2') || '10 user accounts',
        t('landing.pricingSection.professional.feature3') || '100 rooms max',
        t('landing.pricingSection.professional.feature4') || 'WhatsApp & Calendar',
        t('landing.pricingSection.professional.feature5') || 'Police Reports',
        t('landing.pricingSection.professional.feature6') || 'Shift Balance',
        t('landing.pricingSection.professional.feature7') || 'API access',
        t('landing.pricingSection.professional.feature8') || 'Priority support'
      ],
      cta: t('landing.pricingSection.professional.cta'),
      popular: true,
      maxUsers: 10,
      ocrLimit: 600
    },
    {
      name: t('landing.pricingSection.enterprise.name'),
      baseMonthlyPrice: 2899,  // ₹2,899/month base
      description: t('landing.pricingSection.enterprise.description'),
      features: [
        t('landing.pricingSection.enterprise.feature1') || 'Unlimited OCR scans',
        t('landing.pricingSection.enterprise.feature2') || 'Unlimited users',
        t('landing.pricingSection.enterprise.feature3') || 'Unlimited rooms',
        t('landing.pricingSection.enterprise.feature4') || 'Custom branding',
        t('landing.pricingSection.enterprise.feature5') || 'Full API access',
        t('landing.pricingSection.enterprise.feature6') || 'Audit Logs',
        t('landing.pricingSection.enterprise.feature7') || 'Dedicated support',
        t('landing.pricingSection.enterprise.feature8') || 'SLA guarantee',
        'Multi-Hotel Management'
      ],
      cta: t('landing.pricingSection.enterprise.cta'),
      popular: false,
      maxUsers: -1,  // Unlimited
      ocrLimit: -1   // Unlimited
    }
  ];

  // Testimonials - using translations
  const testimonials = [
    {
      quote: t('landing.testimonialsSection.testimonial1.quote'),
      author: t('landing.testimonialsSection.testimonial1.author'),
      role: t('landing.testimonialsSection.testimonial1.role'),
      location: t('landing.testimonialsSection.testimonial1.location'),
      rating: 5,
      avatar: "RK",
      rooms: t('landing.testimonialsSection.testimonial1.rooms')
    },
    {
      quote: t('landing.testimonialsSection.testimonial2.quote'),
      author: t('landing.testimonialsSection.testimonial2.author'),
      role: t('landing.testimonialsSection.testimonial2.role'),
      location: t('landing.testimonialsSection.testimonial2.location'),
      rating: 5,
      avatar: "PS",
      rooms: t('landing.testimonialsSection.testimonial2.rooms')
    },
    {
      quote: t('landing.testimonialsSection.testimonial3.quote'),
      author: t('landing.testimonialsSection.testimonial3.author'),
      role: t('landing.testimonialsSection.testimonial3.role'),
      location: t('landing.testimonialsSection.testimonial3.location'),
      rating: 5,
      avatar: "MA",
      rooms: t('landing.testimonialsSection.testimonial3.rooms')
    }
  ];

  // How it works - using translations
  const howItWorks = [
    {
      step: t('landing.howItWorks.step1.number'),
      title: t('landing.howItWorks.step1.title'),
      description: t('landing.howItWorks.step1.description'),
      icon: <UserCheck className="w-6 h-6" />
    },
    {
      step: t('landing.howItWorks.step2.number'),
      title: t('landing.howItWorks.step2.title'),
      description: t('landing.howItWorks.step2.description'),
      icon: <BedDouble className="w-6 h-6" />
    },
    {
      step: t('landing.howItWorks.step3.number'),
      title: t('landing.howItWorks.step3.title'),
      description: t('landing.howItWorks.step3.description'),
      icon: <Scan className="w-6 h-6" />
    },
    {
      step: t('landing.howItWorks.step4.number'),
      title: t('landing.howItWorks.step4.title'),
      description: t('landing.howItWorks.step4.description'),
      icon: <Smartphone className="w-6 h-6" />
    }
  ];

  // Screenshots/Features showcase - using translations
  const screenshots = [
    { title: t('landing.screenshots.checkInDashboard'), description: t('landing.screenshots.checkInDesc'), type: "desktop" },
    { title: t('landing.screenshots.roomStatus'), description: t('landing.screenshots.roomStatusDesc'), type: "desktop" },
    { title: t('landing.screenshots.gstInvoice'), description: t('landing.screenshots.gstInvoiceDesc'), type: "desktop" },
    { title: t('landing.screenshots.mobileApp'), description: t('landing.screenshots.mobileAppDesc'), type: "mobile" },
  ];

  return (
    <div className="min-h-screen bg-white overflow-x-hidden">
      {/* Skip Link for Accessibility - WCAG 2.1 AA (hidden until focused) */}
      <a 
        href="#main-content" 
        className="sr-only focus:not-sr-only focus:absolute focus:top-0 focus:left-0 focus:z-[100000] focus:bg-gray-900 focus:text-white focus:px-6 focus:py-3 focus:font-semibold focus:rounded-br-lg focus:outline-2 focus:outline-yellow-400"
        onClick={(e) => {
          e.preventDefault();
          document.getElementById('main-content')?.focus();
        }}
      >
        Skip to main content
      </a>

      {/* Navigation */}
      <nav 
        className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-xl border-b border-purple-100/50 shadow-sm"
        role="navigation"
        aria-label="Main navigation"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-20 lg:h-24">
            <Link to="/" className="flex items-center flex-shrink-0" aria-label="Merina HMS - Home">
              <img src="/merina-logo-md.png" alt="Merina HMS - Hotel Management Made Simple" className="h-14 lg:h-16 w-auto object-contain" />
            </Link>

            {/* Desktop Menu */}
            <div className="hidden lg:flex items-center gap-6 xl:gap-8">
              <a href="#features" className="text-gray-600 hover:text-purple-700 transition-colors text-sm font-medium whitespace-nowrap">{t('landing.features') || 'Features'}</a>
              <a href="#pricing" className="text-gray-600 hover:text-purple-700 transition-colors text-sm font-medium whitespace-nowrap">{t('pricing.title') || 'Pricing'}</a>
              <a href="#contact" className="text-gray-600 hover:text-purple-700 transition-colors text-sm font-medium whitespace-nowrap">{t('landing.contact') || 'Contact'}</a>
              <Link to="/cform-portal" className="text-gray-600 hover:text-purple-700 transition-colors text-sm font-medium whitespace-nowrap flex items-center gap-1.5" data-testid="nav-police-portal-link">
                <Shield className="w-3.5 h-3.5" />
                {t('landing.policePortal') || 'Police Portal'}
              </Link>
            </div>

            <div className="hidden lg:flex items-center gap-3 flex-shrink-0">
              <LanguageSwitcher />
              <Link to="/login">
                <Button variant="ghost" className="text-gray-700 hover:text-purple-700 hover:bg-purple-50 font-medium">
                  {t('landing.loginButton') || 'Login'}
                </Button>
              </Link>
              <Link to="/register">
                <Button className="bg-gradient-to-r from-violet-600 via-purple-600 to-indigo-600 hover:from-violet-700 hover:via-purple-700 hover:to-indigo-700 text-white font-semibold shadow-lg shadow-purple-500/25 px-6" data-testid="nav-cta-btn">
                  {t('landing.startTrial') || 'Start Free Trial'}
                </Button>
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <div className="lg:hidden flex items-center gap-2">
              {/* Mobile Language Switcher - Always visible with label */}
              <LanguageSwitcher variant="ghost" size="sm" showLabel={true} alwaysShowFull={true} className="h-10 px-2" />
              
              <button
                className="p-2 text-gray-700"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                aria-label="Toggle menu"
              >
                {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden bg-white/95 backdrop-blur-xl border-t border-purple-100 py-4">
            <div className="px-6 space-y-4">
              <a href="#features" className="block text-gray-700 hover:text-purple-700 py-2 font-medium">{t('landing.features') || 'Features'}</a>
              <a href="#why-merina" className="block text-gray-700 hover:text-purple-700 py-2 font-medium">{t('landing.whyMerina') || 'Why Merina'}</a>
              <a href="#pricing" className="block text-gray-700 hover:text-purple-700 py-2 font-medium">{t('pricing.title') || 'Pricing'}</a>
              <a href="#testimonials" className="block text-gray-700 hover:text-purple-700 py-2 font-medium">{t('landing.testimonials') || 'Testimonials'}</a>
              <a href="#contact" className="block text-gray-700 hover:text-purple-700 py-2 font-medium">{t('landing.contact') || 'Contact'}</a>
              <Link to="/cform-portal" className="block text-gray-700 hover:text-purple-700 py-2 font-medium flex items-center gap-2" data-testid="mobile-police-portal-link">
                <Shield className="w-4 h-4" />
                {t('landing.policePortal') || 'Police Portal'}
              </Link>
              <div className="pt-4 space-y-3 border-t border-purple-100">
                <Link to="/login" className="block">
                  <Button variant="outline" className="w-full border-purple-200 text-purple-700 hover:bg-purple-50">{t('landing.loginButton') || 'Login'}</Button>
                </Link>
                <Link to="/register" className="block">
                  <Button className="w-full bg-gradient-to-r from-violet-600 via-purple-600 to-indigo-600 text-white font-semibold">
                    {t('landing.startTrial') || 'Start Free Trial'}
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section - Sharpened Value Messaging */}
      <section 
        id="main-content"
        className="relative min-h-screen flex items-center pt-20 overflow-hidden"
        role="region"
        aria-label="Hero section"
        tabIndex={-1}
      >
        {/* Luxurious Gradient Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-violet-950 via-purple-900 to-indigo-950" />
        
        {/* Subtle Pattern Overlay */}
        <div className="absolute inset-0 opacity-30">
          <div className="absolute inset-0" style={{
            backgroundImage: `radial-gradient(circle at 20% 30%, rgba(167, 139, 250, 0.2) 0%, transparent 40%),
                              radial-gradient(circle at 80% 70%, rgba(129, 140, 248, 0.15) 0%, transparent 40%),
                              radial-gradient(circle at 50% 50%, rgba(192, 132, 252, 0.1) 0%, transparent 60%)`
          }} />
        </div>
        
        {/* Elegant Grid Pattern */}
        <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: `linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px),
                            linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)`,
          backgroundSize: '60px 60px'
        }} />

        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 py-20 lg:py-32">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            {/* Left Content */}
            <div className="text-center lg:text-left">
              {/* Trust Badge */}
              <div className="inline-flex items-center gap-2 bg-emerald-500/20 backdrop-blur-sm border border-emerald-400/30 rounded-full px-4 py-2 mb-6">
                <ShieldCheck className="w-4 h-4 text-emerald-400" />
                <span className="text-emerald-300 text-sm font-medium">{t('landing.trustedBy') || 'Trusted by 500+ Indian Hotels'}</span>
              </div>
              
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-[1.1] tracking-tight" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                {t('landing.heroTitle') || 'Hotel Management Made'}{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-300 via-pink-300 to-indigo-300">
                  {t('landing.heroHighlight') || 'Simple'}
                </span>
              </h1>
              
              <p className="text-xl text-white font-medium mb-3">
                {t('landing.heroTagline') || 'Mobile, Smart, and India-Ready'}
              </p>
              
              <p className="text-lg text-purple-200/80 mb-8 max-w-xl leading-relaxed">
                {t('landing.heroSubtitle') || 'Run your lodge or boutique hotel with speed — from check-in to billing, UPI/GST-ready, and real-time room control. Built for Indian hospitality.'}
              </p>

              {/* Primary CTA */}
              <div className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto lg:mx-0 mb-4">
                <Link to="/register" className="flex-1">
                  <Button 
                    size="lg"
                    className="w-full h-14 px-8 bg-gradient-to-r from-violet-500 via-purple-500 to-indigo-500 hover:from-violet-600 hover:via-purple-600 hover:to-indigo-600 text-white font-semibold shadow-xl shadow-purple-500/30 rounded-xl text-base"
                    data-testid="hero-cta-btn"
                  >
                    {t('landing.startTrial') || 'Start Free Hotel Trial'}
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
              </div>
              
              {/* Secondary CTA */}
              <div className="flex items-center justify-center lg:justify-start gap-2 mb-8">
                <Rocket className="w-4 h-4 text-emerald-400" />
                <span className="text-emerald-400 font-semibold text-sm">{t('landing.goLive') || 'Go Live in 30 Minutes'}</span>
              </div>

              {/* Trust Indicators */}
              <div className="flex flex-wrap items-center justify-center lg:justify-start gap-x-6 gap-y-3 text-sm">
                <div className="flex items-center gap-2 text-purple-200/80">
                  <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                  <span>{t('landing.noCreditCard')}</span>
                </div>
                <div className="flex items-center gap-2 text-purple-200/80">
                  <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                  <span>{t('landing.14dayTrial')}</span>
                </div>
                <div className="flex items-center gap-2 text-purple-200/80">
                  <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                  <span>{t('landing.gstIncluded')}</span>
                </div>
              </div>
            </div>

            {/* Right - Dashboard Preview */}
            <div className="relative hidden lg:block">
              <div className="relative">
                {/* Glow Effect */}
                <div className="absolute -inset-4 bg-gradient-to-r from-violet-500/20 via-purple-500/20 to-indigo-500/20 rounded-3xl blur-2xl" />
                
                {/* Main Dashboard Card */}
                <div className="relative bg-white/10 backdrop-blur-xl rounded-2xl border border-white/20 shadow-2xl overflow-hidden">
                  <div className="bg-gradient-to-r from-purple-900/50 to-indigo-900/50 px-6 py-4 border-b border-white/10">
                    <div className="flex items-center gap-3">
                      <div className="w-3 h-3 rounded-full bg-rose-400" />
                      <div className="w-3 h-3 rounded-full bg-amber-400" />
                      <div className="w-3 h-3 rounded-full bg-emerald-400" />
                      <span className="ml-4 text-purple-200/80 text-sm font-medium">Dashboard — Merina HMS</span>
                    </div>
                  </div>
                  <div className="p-6">
                    <div className="grid grid-cols-3 gap-4 mb-6">
                      <div className="bg-emerald-500/20 backdrop-blur-sm border border-emerald-400/30 rounded-xl p-4 text-center">
                        <p className="text-3xl font-bold text-emerald-300">24</p>
                        <p className="text-xs text-emerald-200/70 mt-1">{t('landing.available')}</p>
                      </div>
                      <div className="bg-rose-500/20 backdrop-blur-sm border border-rose-400/30 rounded-xl p-4 text-center">
                        <p className="text-3xl font-bold text-rose-300">18</p>
                        <p className="text-xs text-rose-200/70 mt-1">{t('landing.occupied')}</p>
                      </div>
                      <div className="bg-amber-500/20 backdrop-blur-sm border border-amber-400/30 rounded-xl p-4 text-center">
                        <p className="text-3xl font-bold text-amber-300">3</p>
                        <p className="text-xs text-amber-200/70 mt-1">{t('landing.checkout')}</p>
                      </div>
                    </div>
                    <div className="bg-white/5 backdrop-blur-sm rounded-xl p-4 border border-white/10">
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-sm text-purple-200/80">{t('landing.todaysRevenue')}</span>
                        <span className="text-2xl font-bold text-white">₹45,200</span>
                      </div>
                      <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                        <div className="h-full w-3/4 bg-gradient-to-r from-violet-400 via-purple-400 to-indigo-400 rounded-full" />
                      </div>
                      <p className="text-xs text-purple-300/60 mt-2">75% {t('landing.ofDailyTarget')}</p>
                    </div>
                  </div>
                </div>

                {/* Floating Cards */}
                <div className="absolute -top-6 -right-6 bg-white rounded-xl p-4 shadow-2xl border border-purple-100">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-emerald-400 to-emerald-600 rounded-full flex items-center justify-center shadow-lg">
                      <CheckCircle2 className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-900">{t('landing.guestCheckedIn')}</p>
                      <p className="text-xs text-gray-500">{t('landing.roomJustNow')}</p>
                    </div>
                  </div>
                </div>

                <div className="absolute -bottom-4 -left-6 bg-white rounded-xl p-4 shadow-2xl border border-purple-100">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-violet-500 to-purple-600 rounded-full flex items-center justify-center shadow-lg">
                      <IndianRupee className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-900">{t('landing.gstInvoiceGenerated')}</p>
                      <p className="text-xs text-gray-500">{t('landing.invoiceJustNow')}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-purple-300/60">
          <span className="text-xs uppercase tracking-widest">Scroll to explore</span>
          <ChevronDown className="w-5 h-5 animate-bounce" />
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-gradient-to-b from-white to-purple-50/30 border-b border-purple-100/50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
            <div className="text-center">
              <p className="text-4xl lg:text-5xl font-bold bg-gradient-to-r from-violet-600 to-purple-600 bg-clip-text text-transparent" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                <AnimatedCounter end={500} suffix="+" />
              </p>
              <p className="text-gray-600 mt-2 font-medium">Hotels Trust Us</p>
            </div>
            <div className="text-center">
              <p className="text-4xl lg:text-5xl font-bold bg-gradient-to-r from-purple-600 to-indigo-600 bg-clip-text text-transparent" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                <AnimatedCounter end={1} suffix="M+" />
              </p>
              <p className="text-gray-600 mt-2 font-medium">IDs Scanned</p>
            </div>
            <div className="text-center">
              <p className="text-4xl lg:text-5xl font-bold bg-gradient-to-r from-indigo-600 to-violet-600 bg-clip-text text-transparent" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                30
              </p>
              <p className="text-gray-600 mt-2 font-medium">Min Setup Time</p>
            </div>
            <div className="text-center">
              <p className="text-4xl lg:text-5xl font-bold bg-gradient-to-r from-violet-600 to-purple-600 bg-clip-text text-transparent" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                99.5%
              </p>
              <p className="text-gray-600 mt-2 font-medium">OCR Accuracy</p>
            </div>
          </div>
        </div>
      </section>

      {/* Video Walkthrough Section */}
      <section id="walkthrough" className="py-12 sm:py-16 lg:py-24 bg-gradient-to-b from-purple-50/30 to-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 sm:mb-10">
            <span className="text-purple-600 font-semibold text-xs sm:text-sm uppercase tracking-widest">{t('landing.walkthroughSection.badge') || 'Product Walkthrough'}</span>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mt-3 sm:mt-4 mb-3 sm:mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              {t('landing.walkthroughSection.title') || 'See Merina HMS in Action'}
            </h2>
            <p className="text-base sm:text-lg text-gray-600 max-w-2xl mx-auto px-4">
              {t('landing.walkthroughSection.subtitle') || 'Watch a quick walkthrough to see how easy it is to manage your hotel with Merina HMS'}
            </p>
          </div>
          
          {/* Video Container - Responsive with proper aspect ratio */}
          <div className="relative w-full max-w-4xl mx-auto">
            <div className="relative rounded-xl sm:rounded-2xl overflow-hidden shadow-xl sm:shadow-2xl shadow-purple-200/50 border border-purple-100 bg-gray-900">
              {/* Aspect ratio container - 16:9 */}
              <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
                <video
                  key={currentVideoUrl}
                  className="absolute inset-0 w-full h-full object-contain bg-black"
                  controls
                  playsInline
                  preload="metadata"
                  poster={`${API}/api/demo-video/thumbnail`}
                >
                  <source src={currentVideoUrl} type="video/mp4" />
                  {t('landing.walkthroughSection.videoNotSupported') || 'Your browser does not support the video tag.'}
                </video>
              </div>
              
              {/* Language indicator - Responsive positioning */}
              <div className="absolute top-2 right-2 sm:top-4 sm:right-4 bg-black/70 backdrop-blur-sm rounded-full px-2 py-1 sm:px-3 sm:py-1.5 flex items-center gap-1.5 sm:gap-2 z-10">
                <Globe className="w-3 h-3 sm:w-4 sm:h-4 text-white" />
                <span className="text-white text-xs sm:text-sm font-medium">
                  {language === 'ta' ? 'தமிழ்' : language === 'ml' ? 'മലയാളം' : language === 'hi' ? 'हिन्दी' : language === 'kn' ? 'ಕನ್ನಡ' : language === 'te' ? 'తెలుగు' : 'English'}
                </span>
              </div>
            </div>
          </div>
          
          {/* Video language note */}
          <p className="text-center text-xs sm:text-sm text-gray-500 mt-3 sm:mt-4 px-4">
            {t('landing.walkthroughSection.languageNote') || 'Video available in English, Hindi, Tamil, Telugu, Kannada & Malayalam. Select your language from the top menu.'}
          </p>
          
          {/* PRO Setup Details - Expandable Section */}
          <div className="mt-8 sm:mt-10 max-w-2xl mx-auto px-4">
            {/* Teaser text */}
            <p className="text-center text-sm sm:text-base text-gray-600 mb-4">
              {t('landing.proSetup.teaser')}
            </p>
            
            {/* Expandable trigger */}
            <button
              onClick={() => setProSetupExpanded(!proSetupExpanded)}
              className="w-full flex items-center justify-center gap-2 py-3 px-6 bg-gradient-to-r from-purple-50 to-indigo-50 hover:from-purple-100 hover:to-indigo-100 border border-purple-200 rounded-xl transition-all duration-300 group"
              data-testid="pro-setup-toggle"
            >
              <span className="font-semibold text-purple-700">{t('landing.proSetup.toggleButton')}</span>
              <ChevronDown className={`w-5 h-5 text-purple-600 transition-transform duration-300 ${proSetupExpanded ? 'rotate-180' : ''}`} />
            </button>
            
            {/* Expanded content */}
            <div className={`overflow-hidden transition-all duration-500 ease-in-out ${proSetupExpanded ? 'max-h-[1200px] opacity-100 mt-6' : 'max-h-0 opacity-0'}`}>
              <div className="bg-white rounded-2xl border border-purple-200 shadow-lg shadow-purple-500/10 p-6 sm:p-8">
                {/* Header */}
                <div className="text-center mb-6">
                  <div className="inline-flex items-center gap-2 mb-3">
                    <h3 className="text-xl sm:text-2xl font-bold text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                      {t('landing.proSetup.title')}
                    </h3>
                    <Badge className="bg-gradient-to-r from-amber-500 to-orange-500 text-white border-0 text-xs">
                      {t('landing.proSetup.badge')}
                    </Badge>
                  </div>
                  <p className="text-gray-600 text-sm sm:text-base">
                    {t('landing.proSetup.subtitle')}
                  </p>
                </div>
                
                {/* 5 Simple Steps */}
                <div className="space-y-4">
                  {/* Step 1 */}
                  <div className="flex items-start gap-4 p-4 bg-purple-50/50 rounded-xl">
                    <div className="w-8 h-8 bg-purple-600 text-white rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0">
                      1
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">{t('landing.proSetup.step1.title')}</h4>
                      <p className="text-sm text-gray-600">{t('landing.proSetup.step1.description')}</p>
                    </div>
                  </div>
                  
                  {/* Step 2 */}
                  <div className="flex items-start gap-4 p-4 bg-purple-50/50 rounded-xl">
                    <div className="w-8 h-8 bg-purple-600 text-white rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0">
                      2
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">{t('landing.proSetup.step2.title')}</h4>
                      <p className="text-sm text-gray-600">{t('landing.proSetup.step2.description')}</p>
                    </div>
                  </div>
                  
                  {/* Step 3 */}
                  <div className="flex items-start gap-4 p-4 bg-purple-50/50 rounded-xl">
                    <div className="w-8 h-8 bg-purple-600 text-white rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0">
                      3
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">{t('landing.proSetup.step3.title')}</h4>
                      <p className="text-sm text-gray-600">{t('landing.proSetup.step3.description')}</p>
                    </div>
                  </div>
                  
                  {/* Step 4 */}
                  <div className="flex items-start gap-4 p-4 bg-purple-50/50 rounded-xl">
                    <div className="w-8 h-8 bg-purple-600 text-white rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0">
                      4
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">{t('landing.proSetup.step4.title')}</h4>
                      <p className="text-sm text-gray-600">{t('landing.proSetup.step4.description')}</p>
                    </div>
                  </div>
                  
                  {/* Step 5 */}
                  <div className="flex items-start gap-4 p-4 bg-green-50 rounded-xl border border-green-200">
                    <div className="w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0">
                      ✓
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">{t('landing.proSetup.step5.title')}</h4>
                      <p className="text-sm text-gray-600">{t('landing.proSetup.step5.description')}</p>
                    </div>
                  </div>
                </div>
                
                {/* CTA */}
                <div className="mt-8 text-center">
                  <Button
                    onClick={() => navigate('/register')}
                    className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white px-8 py-3 rounded-xl shadow-lg shadow-purple-500/25 hover:shadow-xl hover:shadow-purple-500/30 transition-all"
                    data-testid="upgrade-to-pro-btn"
                  >
                    <Sparkles className="w-5 h-5 mr-2" />
                    {t('landing.proSetup.cta')}
                  </Button>
                  <p className="text-xs sm:text-sm text-gray-500 mt-3">
                    {t('landing.proSetup.ctaSubtext')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 lg:py-32 bg-gradient-to-b from-purple-50/30 to-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-purple-600 font-semibold text-sm uppercase tracking-widest">{t('landing.features')}</span>
            <h2 className="text-3xl lg:text-5xl font-bold text-gray-900 mt-4 mb-6" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              {t('landing.featureSection.title')}
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              {t('landing.featureSection.subtitle')}
            </p>
          </div>

          {/* Feature Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <div
                key={`feature-${feature.subtitle}`}
                className={`group relative bg-white rounded-2xl p-8 border border-purple-100 hover:border-purple-300 shadow-sm hover:shadow-xl hover:shadow-purple-500/10 transition-all duration-300 ${
                  index === 0 ? 'lg:col-span-2' : ''
                }`}
                data-testid={`feature-card-${index}`}
              >
                <div className="w-14 h-14 bg-gradient-to-br from-violet-500 via-purple-500 to-indigo-500 rounded-xl flex items-center justify-center mb-6 shadow-lg shadow-purple-500/25 group-hover:scale-110 transition-transform">
                  <div className="text-white">
                    {feature.icon}
                  </div>
                </div>
                
                <div className="flex items-center gap-3 mb-3">
                  <h3 className="text-xl font-bold text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                    {feature.title}
                  </h3>
                  <Badge variant="secondary" className="bg-purple-100 text-purple-700 text-xs">{feature.subtitle}</Badge>
                </div>
                
                <p className="text-gray-600 mb-6 leading-relaxed">{feature.description}</p>
                
                <div className="space-y-2">
                  {feature.highlights.map((highlight, i) => (
                    <div key={`${highlight.slice(0, 20)}-${i}`} className="flex items-center gap-2 text-sm text-gray-500">
                      <CheckCircle2 className="w-4 h-4 text-purple-500 flex-shrink-0" />
                      <span>{highlight}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Merina HMS Comparison Section */}
      <section id="why-merina" className="py-20 lg:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-purple-600 font-semibold text-sm uppercase tracking-widest">{t('landing.whyMerina')}</span>
            <h2 className="text-3xl lg:text-5xl font-bold text-gray-900 mt-4 mb-6" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              {t('landing.whyMerinaSection.title')}
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              {t('landing.whyMerinaSection.subtitle')}
            </p>
          </div>

          {/* Comparison Table */}
          <div className="max-w-4xl mx-auto">
            <div className="bg-gradient-to-br from-purple-50 to-indigo-50/50 rounded-2xl border border-purple-100 overflow-hidden shadow-lg">
              {/* Header */}
              <div className="grid grid-cols-3 bg-gradient-to-r from-violet-600 via-purple-600 to-indigo-600 text-white">
                <div className="p-4 lg:p-6 font-semibold">{t('landing.whyMerinaSection.comparison.feature')}</div>
                <div className="p-4 lg:p-6 font-semibold text-center border-l border-white/20">
                  <div className="flex items-center justify-center gap-2">
                    <Hotel className="w-5 h-5" />
                    {t('landing.whyMerinaSection.comparison.merina')}
                  </div>
                </div>
                <div className="p-4 lg:p-6 font-semibold text-center border-l border-white/20">{t('landing.whyMerinaSection.comparison.traditional')}</div>
              </div>
              
              {/* Rows */}
              {comparisonData.map((row, index) => (
                <div key={`compare-${row.feature}`} className={`grid grid-cols-3 ${index % 2 === 0 ? 'bg-white' : 'bg-purple-50/50'}`}>
                  <div className="p-4 lg:p-5 text-gray-700 font-medium flex items-center gap-2">
                    {row.feature}
                  </div>
                  <div className="p-4 lg:p-5 text-center border-l border-purple-100 flex items-center justify-center">
                    {row.merina === true ? (
                      <div className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center">
                        <Check className="w-5 h-5 text-emerald-600" />
                      </div>
                    ) : typeof row.merina === 'string' ? (
                      <span className="font-semibold text-purple-600">{row.merina}</span>
                    ) : (
                      <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
                        <XIcon className="w-5 h-5 text-gray-400" />
                      </div>
                    )}
                  </div>
                  <div className="p-4 lg:p-5 text-center border-l border-purple-100 flex items-center justify-center">
                    {row.traditional === true ? (
                      <div className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center">
                        <Check className="w-5 h-5 text-emerald-600" />
                      </div>
                    ) : row.traditional === "partial" ? (
                      <span className="text-amber-600 font-medium text-sm">{t('landing.whyMerinaSection.comparison.partial')}</span>
                    ) : typeof row.traditional === 'string' ? (
                      <span className="text-gray-500">{row.traditional}</span>
                    ) : (
                      <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
                        <XIcon className="w-5 h-5 text-gray-400" />
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* CTA under comparison */}
            <div className="text-center mt-10">
              <Link to="/register">
                <Button size="lg" className="h-14 px-10 bg-gradient-to-r from-violet-600 via-purple-600 to-indigo-600 hover:from-violet-700 hover:via-purple-700 hover:to-indigo-700 text-white font-semibold shadow-lg shadow-purple-500/25 rounded-xl" data-testid="comparison-cta">
                  Start Your Free Hotel Trial
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <p className="text-sm text-gray-500 mt-3">No setup fee • No credit card needed</p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="py-20 lg:py-32 bg-gradient-to-b from-white to-purple-50/30">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-purple-600 font-semibold text-sm uppercase tracking-widest">{t('landing.howItWorks.title')}</span>
            <h2 className="text-3xl lg:text-5xl font-bold text-gray-900 mt-4 mb-6" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              {t('landing.howItWorks.heading')}
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              {t('landing.howItWorks.subtitle')}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {howItWorks.map((item, index) => (
              <div key={`step-${item.step}`} className="relative">
                <div className="text-center">
                  <div className="relative inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-violet-500 via-purple-500 to-indigo-500 text-white rounded-2xl mb-6 shadow-lg shadow-purple-500/25">
                    {item.icon}
                    <div className="absolute -top-2 -right-2 w-7 h-7 bg-white rounded-full flex items-center justify-center text-purple-600 font-bold text-xs shadow-lg border border-purple-100">
                      {item.step}
                    </div>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-3" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                    {item.title}
                  </h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{item.description}</p>
                </div>
                
                {index < howItWorks.length - 1 && (
                  <div className="hidden lg:block absolute top-8 left-full w-full">
                    <div className="border-t-2 border-dashed border-purple-200 w-full" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* See Merina in Action - Screenshots */}
      <section className="py-20 lg:py-32 bg-gradient-to-br from-violet-950 via-purple-900 to-indigo-950 overflow-hidden relative">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute inset-0" style={{
            backgroundImage: `radial-gradient(circle at 30% 40%, rgba(167, 139, 250, 0.3) 0%, transparent 50%)`
          }} />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-purple-300 font-semibold text-sm uppercase tracking-widest">{t('landing.screenshots.seeItInAction')}</span>
            <h2 className="text-3xl lg:text-5xl font-bold text-white mt-4 mb-6" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              {t('landing.screenshots.title')}
            </h2>
            <p className="text-lg text-purple-200/80 max-w-2xl mx-auto">
              {t('landing.screenshots.subtitle')}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {screenshots.map((screen, index) => (
              <div key={`screen-${screen.title}`} className="group">
                <div className={`bg-white/10 backdrop-blur-xl rounded-2xl border border-white/20 overflow-hidden hover:border-purple-400/50 transition-all ${
                  screen.type === 'mobile' ? 'max-w-[200px] mx-auto' : ''
                }`}>
                  {/* Mock Screen Header */}
                  <div className="bg-white/5 px-4 py-2 border-b border-white/10">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-rose-400" />
                      <div className="w-2 h-2 rounded-full bg-amber-400" />
                      <div className="w-2 h-2 rounded-full bg-emerald-400" />
                    </div>
                  </div>
                  
                  {/* Screen Content Placeholder */}
                  <div className={`bg-gradient-to-br from-purple-900/50 to-indigo-900/50 ${
                    screen.type === 'mobile' ? 'aspect-[9/16]' : 'aspect-video'
                  } flex items-center justify-center`}>
                    <div className="text-center p-4">
                      {screen.type === 'mobile' ? (
                        <Smartphone className="w-12 h-12 text-purple-300 mx-auto mb-3" />
                      ) : (
                        <Monitor className="w-12 h-12 text-purple-300 mx-auto mb-3" />
                      )}
                      <p className="text-white font-medium text-sm">{screen.title}</p>
                    </div>
                  </div>
                </div>
                <p className="text-center text-purple-200/70 text-sm mt-3">{screen.description}</p>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/register">
              <Button size="lg" className="h-14 px-10 bg-white text-purple-700 hover:bg-purple-50 font-semibold shadow-xl rounded-xl" data-testid="screenshots-cta">
                {t('landing.startTrial')}
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <p className="text-purple-300/60 text-sm mt-3">{t('landing.noCreditCard')} • {t('landing.goLive')}</p>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="py-20 lg:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-6">
            <span className="text-purple-600 font-semibold text-sm uppercase tracking-widest">{t('landing.testimonials')}</span>
            <h2 className="text-3xl lg:text-5xl font-bold text-gray-900 mt-4 mb-6" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              {t('landing.testimonialsSection.title')}
            </h2>
            <p className="text-lg text-gray-600">
              {t('landing.testimonialsSection.subtitle')}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-12">
            {testimonials.map((testimonial, index) => (
              <div
                key={`testimonial-${testimonial.name || index}`}
                className="bg-gradient-to-br from-purple-50 to-indigo-50/50 rounded-2xl p-8 border border-purple-100 hover:shadow-xl hover:shadow-purple-500/10 transition-all"
                data-testid={`testimonial-card-${index}`}
              >
                <Quote className="w-10 h-10 text-purple-400 mb-6" />
                
                <div className="flex gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={`star-${i}`} className="w-5 h-5 fill-purple-500 text-purple-500" />
                  ))}
                </div>
                
                <p className="text-gray-700 mb-6 leading-relaxed">&ldquo;{testimonial.quote}&rdquo;</p>
                
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-violet-500 via-purple-500 to-indigo-500 rounded-full flex items-center justify-center text-white font-bold shadow-lg">
                    {testimonial.avatar}
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">{testimonial.author}</p>
                    <p className="text-sm text-gray-600">{testimonial.role}</p>
                    <p className="text-xs text-purple-600">{testimonial.location} • {testimonial.rooms}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Founder Note */}
          <div className="max-w-3xl mx-auto bg-gradient-to-r from-violet-600 via-purple-600 to-indigo-600 rounded-2xl p-8 text-white text-center shadow-xl">
            <Heart className="w-10 h-10 mx-auto mb-4 text-purple-200" />
            <p className="text-lg mb-4 text-purple-100">
              &ldquo;{t('landing.testimonialsSection.founderNote')}&rdquo;
            </p>
            <p className="font-semibold">{t('landing.testimonialsSection.founderSignature')}</p>
            <p className="text-sm text-purple-200">{t('landing.testimonialsSection.founderSubtext')}</p>
          </div>
        </div>
      </section>

      {/* Multi-Hotel Enterprise Feature Section */}
      <section id="enterprise" className="py-20 lg:py-28 bg-gradient-to-br from-violet-950 via-purple-900 to-indigo-950 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,rgba(167,139,250,0.15),transparent_50%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_80%,rgba(129,140,248,0.1),transparent_50%)]" />
        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div className="space-y-8">
              <div>
                <span className="text-purple-400 font-semibold text-xs uppercase tracking-[0.2em] bg-purple-500/10 px-3 py-1.5 rounded-full border border-purple-500/20">
                  Enterprise Exclusive
                </span>
                <h2 className="text-3xl lg:text-4xl font-bold mt-6 leading-tight" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                  Multi-Hotel Command Center
                </h2>
                <p className="text-gray-400 mt-4 text-base leading-relaxed max-w-lg">
                  Own multiple properties? Manage them all from a single dashboard. Compare revenue, track occupancy, and switch between hotels instantly — available exclusively on Trial & Enterprise plans.
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {[
                  { icon: 'chart', title: 'Revenue Comparison', desc: 'Side-by-side revenue charts across all properties' },
                  { icon: 'switch', title: 'Instant Hotel Switching', desc: 'One-click context switch between properties' },
                  { icon: 'gauge', title: 'Live Occupancy Tracking', desc: 'Real-time occupancy rates with auto-refresh' },
                  { icon: 'shield', title: 'Data Isolation', desc: 'Complete tenant separation for each property' },
                ].map((feat, i) => (
                  <div key={`enterprise-${i}`} className="flex items-start gap-3 bg-white/5 rounded-xl p-4 border border-white/10 hover:border-purple-400/30 transition-colors" data-testid={`enterprise-feature-${i}`}>
                    <div className="w-9 h-9 rounded-lg bg-purple-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                      {feat.icon === 'chart' && <BarChart3 className="w-4 h-4 text-purple-400" />}
                      {feat.icon === 'switch' && <Building2 className="w-4 h-4 text-purple-400" />}
                      {feat.icon === 'gauge' && <TrendingUp className="w-4 h-4 text-purple-400" />}
                      {feat.icon === 'shield' && <Shield className="w-4 h-4 text-purple-400" />}
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-white">{feat.title}</p>
                      <p className="text-xs text-purple-200/40 mt-0.5">{feat.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              <Link to="/register">
                <Button size="lg" className="bg-white text-purple-700 hover:bg-purple-50 font-semibold shadow-xl shadow-purple-600/20 mt-2" data-testid="enterprise-cta-btn">
                  <Crown className="w-4 h-4 mr-2" />
                  Start 14-Day Free Trial
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
            <div className="relative">
              <div className="bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 p-6 shadow-2xl">
                <div className="flex items-center gap-2 mb-6">
                  <div className="w-3 h-3 rounded-full bg-red-400" />
                  <div className="w-3 h-3 rounded-full bg-amber-400" />
                  <div className="w-3 h-3 rounded-full bg-emerald-400" />
                  <span className="text-[11px] text-purple-300/60 ml-2 font-mono">Enterprise Dashboard</span>
                </div>
                <div className="space-y-4">
                  <div className="grid grid-cols-4 gap-3">
                    {[
                      { label: 'Revenue', value: '₹12.4L', color: 'text-emerald-400' },
                      { label: 'Rooms', value: '248', color: 'text-blue-400' },
                      { label: 'Bookings', value: '89', color: 'text-purple-400' },
                      { label: 'Occupancy', value: '76%', color: 'text-amber-400' },
                    ].map((stat) => (
                      <div key={stat.label} className="bg-white/5 rounded-lg p-3 text-center">
                        <p className={`text-lg font-bold ${stat.color}`}>{stat.value}</p>
                        <p className="text-[10px] text-purple-200/50 mt-0.5">{stat.label}</p>
                      </div>
                    ))}
                  </div>
                  <div className="space-y-2">
                    {['Merina Towers - Chennai', 'Merina Residency - Madurai', 'Merina Suites - Coimbatore'].map((name, i) => (
                      <div key={name} className="flex items-center gap-3 bg-white/5 rounded-lg px-3 py-2.5">
                        <div className={`w-2 h-2 rounded-full ${i === 0 ? 'bg-purple-400' : i === 1 ? 'bg-blue-400' : 'bg-emerald-400'}`} />
                        <span className="text-sm text-purple-100/80 flex-1">{name}</span>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-purple-200/50">{[82, 71, 68][i]}%</span>
                          <div className="w-16 h-1.5 bg-white/10 rounded-full overflow-hidden">
                            <div className={`h-full rounded-full ${i === 0 ? 'bg-purple-500' : i === 1 ? 'bg-blue-500' : 'bg-emerald-500'}`} style={{ width: `${[82, 71, 68][i]}%` }} />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="flex items-center justify-between pt-2 border-t border-white/5">
                    <span className="text-[10px] text-purple-300/40 flex items-center gap-1">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                      Live · Auto-refresh 30s
                    </span>
                    <span className="text-[10px] text-purple-400 font-medium">3 Properties</span>
                  </div>
                </div>
              </div>
              <div className="absolute -bottom-4 -right-4 w-32 h-32 bg-purple-600/10 rounded-full blur-3xl" />
              <div className="absolute -top-4 -left-4 w-24 h-24 bg-indigo-600/10 rounded-full blur-3xl" />
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 lg:py-32 bg-gradient-to-b from-white to-purple-50/30">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-purple-600 font-semibold text-sm uppercase tracking-widest">{t('pricing.title')}</span>
            <h2 className="text-3xl lg:text-5xl font-bold text-gray-900 mt-4 mb-6" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              {t('landing.pricingSection.title')}
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto mb-8">
              {t('landing.pricingSection.subtitle')}
            </p>
            
            {/* Billing Cycle Selector - Quarterly / Half-Yearly / Yearly */}
            <div className="flex items-center justify-center gap-2 bg-gray-100 p-1 rounded-xl max-w-md mx-auto">
              <button
                onClick={() => setBillingCycle('quarterly')}
                className={`px-4 py-2 rounded-lg font-medium text-sm transition-all ${
                  billingCycle === 'quarterly' 
                    ? 'bg-white text-purple-700 shadow-md' 
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                {t('landing.pricingSection.quarterly') || 'Quarterly'}
              </button>
              <button
                onClick={() => setBillingCycle('half_yearly')}
                className={`px-4 py-2 rounded-lg font-medium text-sm transition-all relative ${
                  billingCycle === 'half_yearly' 
                    ? 'bg-white text-purple-700 shadow-md' 
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                {t('landing.pricingSection.halfYearly') || 'Half-Yearly'}
                <Badge className="absolute -top-2 -right-2 bg-emerald-500 text-white text-[10px] px-1.5">10%</Badge>
              </button>
              <button
                onClick={() => setBillingCycle('yearly')}
                className={`px-4 py-2 rounded-lg font-medium text-sm transition-all relative ${
                  billingCycle === 'yearly' 
                    ? 'bg-white text-purple-700 shadow-md' 
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                {t('landing.pricingSection.yearly') || 'Yearly'}
                <Badge className="absolute -top-2 -right-2 bg-emerald-500 text-white text-[10px] px-1.5">20%</Badge>
              </button>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto items-stretch">
            {pricingPlans.map((plan, index) => (
              <div
                key={`plan-${plan.name}`}
                className={`relative bg-white rounded-2xl p-8 transition-all duration-300 flex flex-col ${
                  plan.popular
                    ? 'border-2 border-purple-500 shadow-2xl shadow-purple-500/20 scale-105 z-10'
                    : 'border border-purple-100 hover:border-purple-300 hover:shadow-xl hover:shadow-purple-500/10'
                }`}
                data-testid={`pricing-card-${plan.name.toLowerCase()}`}
              >
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                    <Badge className="bg-gradient-to-r from-violet-600 via-purple-600 to-indigo-600 text-white font-semibold px-4 py-1 shadow-lg">
                      {t('landing.pricingSection.mostPopular')}
                    </Badge>
                  </div>
                )}
                
                <div className="text-center mb-8">
                  <h3 className="text-xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>{plan.name}</h3>
                  <p className="text-gray-500 text-sm mb-4">{plan.description}</p>
                  
                  {/* Full Price Display */}
                  <div className="flex items-baseline justify-center gap-1">
                    <span className="text-4xl font-bold text-gray-900">
                      ₹{plan.baseMonthlyPrice.toLocaleString('en-IN')}
                    </span>
                    <span className="text-gray-500">/mo</span>
                  </div>
                  
                  {/* Total for billing cycle */}
                  <p className="text-sm text-gray-500 mt-2">
                    ₹{getPriceForCycle(plan.baseMonthlyPrice, billingCycle).toLocaleString('en-IN')} for {getCycleLabel(billingCycle)}
                  </p>
                  {getDiscountBadge(billingCycle) && (
                    <Badge className="mt-2 bg-emerald-100 text-emerald-700 text-xs">
                      {getDiscountBadge(billingCycle)}
                    </Badge>
                  )}
                  
                  {/* User and OCR limits */}
                  <div className="flex justify-center gap-4 mt-3 text-xs text-gray-500">
                    <span>{plan.maxUsers === -1 ? 'Unlimited' : plan.maxUsers} users</span>
                    <span>•</span>
                    <span>{plan.ocrLimit === -1 ? 'Unlimited' : plan.ocrLimit} OCR/mo</span>
                  </div>
                </div>

                <ul className="space-y-4 mb-8 flex-1">
                  {plan.features.map((feature, i) => (
                    <li key={`${feature.slice(0, 20)}-${i}`} className="flex items-center gap-3">
                      <CheckCircle2 className="w-5 h-5 text-purple-500 flex-shrink-0" />
                      <span className="text-gray-600 text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>

                <div className="mt-auto">
                  <Link to="/register" className="block">
                    <Button
                      className={`w-full h-12 font-semibold rounded-xl ${
                        plan.popular
                          ? 'bg-gradient-to-r from-violet-600 via-purple-600 to-indigo-600 hover:from-violet-700 hover:via-purple-700 hover:to-indigo-700 text-white shadow-lg shadow-purple-500/25'
                          : 'bg-gray-900 hover:bg-gray-800 text-white'
                      }`}
                      data-testid={`pricing-cta-${plan.name.toLowerCase()}`}
                    >
                      {plan.cta}
                    </Button>
                  </Link>
                  <p className="text-center text-xs text-gray-500 mt-3">{t('landing.pricingSection.noCreditCard')}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-20 lg:py-32 bg-gradient-to-br from-violet-950 via-purple-900 to-indigo-950 relative overflow-hidden">
        <div className="absolute inset-0 opacity-30">
          <div className="absolute inset-0" style={{
            backgroundImage: `radial-gradient(circle at 50% 50%, rgba(167, 139, 250, 0.3) 0%, transparent 50%)`
          }} />
        </div>
        
        <div className="relative max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-3xl lg:text-5xl font-bold text-white mb-6" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            {t('landing.finalCta.title')}
          </h2>
          <p className="text-lg text-purple-200/80 mb-10 max-w-2xl mx-auto">
            {t('landing.finalCta.subtitle')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/register">
              <Button size="lg" className="h-14 px-10 bg-white text-purple-700 hover:bg-purple-50 font-semibold shadow-xl rounded-xl" data-testid="final-cta-btn">
                {t('landing.startTrial')}
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <a href="#contact">
              <Button size="lg" variant="outline" className="h-14 px-10 border-white/30 text-white hover:bg-white/10 rounded-xl">
                {t('landing.finalCta.talkToSales')}
              </Button>
            </a>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-6 mt-8 text-sm text-purple-200/70">
            <span className="flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-400" />
              {t('landing.finalCta.noSetupFee')}
            </span>
            <span className="flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-400" />
              {t('landing.finalCta.noCreditCard')}
            </span>
            <span className="flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-400" />
              {t('landing.finalCta.gstInvoicing')}
            </span>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 lg:py-32 bg-gradient-to-b from-white to-purple-50/30">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20">
            <div>
              <span className="text-purple-600 font-semibold text-sm uppercase tracking-widest">{t('landing.contact')}</span>
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mt-4 mb-6" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                {t('landing.contactSection.title')}
              </h2>
              <p className="text-lg text-gray-600 mb-10">
                {t('landing.contactSection.subtitle')}
              </p>
              
              <div className="space-y-6">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 bg-gradient-to-br from-violet-100 to-purple-100 rounded-xl flex items-center justify-center">
                    <Mail className="w-6 h-6 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 mb-1">{t('landing.contactSection.emailUs')}</p>
                    <a href="mailto:support@merinahms.in" className="text-gray-900 font-semibold hover:text-purple-600 transition-colors">
                      support@merinahms.in
                    </a>
                  </div>
                </div>
                
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 bg-gradient-to-br from-violet-100 to-purple-100 rounded-xl flex items-center justify-center">
                    <Phone className="w-6 h-6 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 mb-1">{t('landing.contactSection.callUs')}</p>
                    <a href="tel:+919944070337" className="text-gray-900 font-semibold hover:text-purple-600 transition-colors">
                      +91-9944070337
                    </a>
                  </div>
                </div>
                
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 bg-gradient-to-br from-violet-100 to-purple-100 rounded-xl flex items-center justify-center">
                    <MapPin className="w-6 h-6 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 mb-1">{t('landing.contactSection.visitUs')}</p>
                    <p className="text-gray-900 font-medium">
                      {t('landing.contactSection.address')}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl p-8 lg:p-10 shadow-xl shadow-purple-500/10 border border-purple-100">
              <h3 className="text-xl font-bold text-gray-900 mb-6" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>{t('landing.contactSection.sendMessage')}</h3>
              <form onSubmit={handleContactSubmit} className="space-y-5" data-testid="contact-form">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">{t('landing.contactSection.nameLabel')}</label>
                    <Input 
                      name="name"
                      value={contactForm.name}
                      onChange={handleContactChange}
                      placeholder={t('landing.contactSection.namePlaceholder')} 
                      className="h-12 bg-purple-50/50 border-purple-100 focus:border-purple-400 focus:ring-purple-400/30 rounded-xl"
                      required
                      data-testid="contact-name-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">{t('landing.contactSection.hotelNameLabel')}</label>
                    <Input 
                      name="hotel_name"
                      value={contactForm.hotel_name}
                      onChange={handleContactChange}
                      placeholder={t('landing.contactSection.hotelPlaceholder')} 
                      className="h-12 bg-purple-50/50 border-purple-100 focus:border-purple-400 focus:ring-purple-400/30 rounded-xl"
                      data-testid="contact-hotel-input"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('landing.contactSection.emailLabel')}</label>
                  <Input 
                    type="email" 
                    name="email"
                    value={contactForm.email}
                    onChange={handleContactChange}
                    placeholder={t('landing.contactSection.emailPlaceholder')} 
                    className="h-12 bg-purple-50/50 border-purple-100 focus:border-purple-400 focus:ring-purple-400/30 rounded-xl"
                    required
                    data-testid="contact-email-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('landing.contactSection.phoneLabel')}</label>
                  <Input 
                    type="tel" 
                    name="phone"
                    value={contactForm.phone}
                    onChange={handleContactChange}
                    placeholder={t('landing.contactSection.phonePlaceholder')} 
                    className="h-12 bg-purple-50/50 border-purple-100 focus:border-purple-400 focus:ring-purple-400/30 rounded-xl"
                    data-testid="contact-phone-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('landing.contactSection.messageLabel')}</label>
                  <textarea
                    name="message"
                    value={contactForm.message}
                    onChange={handleContactChange}
                    rows={4}
                    placeholder={t('landing.contactSection.messagePlaceholder')}
                    className="w-full px-4 py-3 bg-purple-50/50 border border-purple-100 rounded-xl focus:ring-2 focus:ring-purple-400/30 focus:border-purple-400 resize-none transition-all"
                    required
                    data-testid="contact-message-input"
                  />
                </div>
                <Button 
                  type="submit" 
                  className="w-full h-12 bg-gradient-to-r from-violet-600 via-purple-600 to-indigo-600 hover:from-violet-700 hover:via-purple-700 hover:to-indigo-700 text-white font-semibold rounded-xl shadow-lg shadow-purple-500/25"
                  disabled={contactLoading}
                  data-testid="contact-submit-btn"
                >
                  {contactLoading ? t('landing.contactSection.sending') : t('landing.contactSection.sendButton')}
                </Button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gradient-to-br from-violet-950 via-purple-950 to-indigo-950 text-white py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
            <div>
              <div className="flex items-center gap-3 mb-6">
                <div className="bg-white rounded-2xl p-3 shadow-lg">
                  <img src="/merina-logo-md.png" alt="Merina HMS" className="h-14 sm:h-16 w-auto object-contain" />
                </div>
              </div>
              <p className="text-purple-200/70 leading-relaxed mb-4">
                {t('landing.footer.description')}
              </p>
              <div className="flex items-center gap-2 text-emerald-400 text-sm">
                <ShieldCheck className="w-4 h-4" />
                <span>{t('landing.footer.trustedBy')}</span>
              </div>
            </div>
            
            <div>
              <h4 className="font-semibold mb-6 text-white">{t('landing.footer.product')}</h4>
              <ul className="space-y-3 text-purple-200/70">
                <li><a href="#features" className="hover:text-white transition-colors">{t('landing.features')}</a></li>
                <li><a href="#pricing" className="hover:text-white transition-colors">{t('pricing.title')}</a></li>
                <li><Link to="/register" className="hover:text-white transition-colors">{t('landing.footer.freeTrial')}</Link></li>
                <li><Link to="/login" className="hover:text-white transition-colors">{t('landing.loginButton')}</Link></li>
                <li><Link to="/cform-portal" className="hover:text-white transition-colors flex items-center gap-2" data-testid="footer-police-portal-link"><Shield className="w-3.5 h-3.5" />{t('landing.policePortal') || 'Police Portal'}</Link></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold mb-6 text-white">{t('landing.footer.company')}</h4>
              <ul className="space-y-3 text-purple-200/70">
                <li><a href="#" className="hover:text-white transition-colors">{t('landing.footer.aboutUs')}</a></li>
                <li><Link to="/contact-us" className="hover:text-white transition-colors">{t('landing.contact')}</Link></li>
                <li><Link to="/privacy-policy" className="hover:text-white transition-colors">{t('landing.privacyPolicy')}</Link></li>
                <li><Link to="/terms-and-conditions" className="hover:text-white transition-colors">{t('landing.termsOfService')}</Link></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold mb-6 text-white">{t('landing.footer.support')}</h4>
              <ul className="space-y-3 text-purple-200/70">
                <li>
                  <a href="mailto:support@merinahms.in" className="hover:text-white transition-colors flex items-center gap-2">
                    <Mail className="w-4 h-4" />
                    support@merinahms.in
                  </a>
                </li>
                <li>
                  <a href="tel:+919944070337" className="hover:text-white transition-colors flex items-center gap-2">
                    <Phone className="w-4 h-4" />
                    +91-9944070337
                  </a>
                </li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold mb-6 text-white">Legal</h4>
              <ul className="space-y-3 text-purple-200/70">
                <li><Link to="/cancellation-refund" className="hover:text-white transition-colors">Cancellation & Refund</Link></li>
                <li><Link to="/shipping-exchange" className="hover:text-white transition-colors">Shipping & Exchange</Link></li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-purple-800/50 pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-purple-300/60 text-sm">
              {t('landing.copyright')}
            </p>
            <p className="text-purple-300/60 text-sm flex items-center gap-2">
              {t('landing.footer.madeWith')} <span className="text-rose-400">❤</span> {t('landing.footer.inTrichy')}
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
